import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { FlashcardSetSummary } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import { GraduationCap, Layers } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    sets?: FlashcardSetSummary[];
    levels?: string[];
    themes?: string[];
}

type Filter =
    | { type: 'all' }
    | { type: 'level'; value: string }
    | { type: 'theme'; value: string };

const Chip: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => {
    // theme-primary stays vivid in both light and dark; opacity tints keep the
    // inactive state readable on either background.
    return (
        <button
            type="button"
            onClick={onClick}
            className={clsx(
                'rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors',
                active
                    ? 'border-transparent bg-theme-primary text-theme-on-primary'
                    : 'border-theme-border bg-theme-primary/10 text-theme-primary-text hover:bg-theme-primary/20 dark:text-neutral-100',
            )}
        >
            {children}
        </button>
    );
};

const IndexContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { sets = [], levels = [], themes = [] } = props;

    const [filter, setFilter] = useState<Filter>({ type: 'all' });

    const filteredSets = useMemo(() => {
        if (filter.type === 'level') {
            return sets.filter((set) => set.level === filter.value);
        }
        if (filter.type === 'theme') {
            return sets.filter((set) => set.theme === filter.value);
        }
        return sets;
    }, [sets, filter]);

    const isActive = (candidate: Filter) =>
        candidate.type === filter.type &&
        (candidate.type === 'all' ||
            ('value' in filter && 'value' in candidate && filter.value === candidate.value));

    return (
        <AppLayout>
            <PageTitle title={t('flashcards.title')} />
            <div className="mx-auto max-w-6xl space-y-8">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    {t('flashcards.chooseSet')}
                </h1>

                {/* Filters */}
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Chip
                            active={isActive({ type: 'all' })}
                            onClick={() => setFilter({ type: 'all' })}
                        >
                            {t('flashcards.all')}
                        </Chip>
                        {levels.map((level) => (
                            <Chip
                                key={level}
                                active={isActive({ type: 'level', value: level })}
                                onClick={() => setFilter({ type: 'level', value: level })}
                            >
                                {level}
                            </Chip>
                        ))}
                    </div>
                    {themes.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                            {themes.map((theme) => (
                                <Chip
                                    key={theme}
                                    active={isActive({ type: 'theme', value: theme })}
                                    onClick={() => setFilter({ type: 'theme', value: theme })}
                                >
                                    {theme}
                                </Chip>
                            ))}
                        </div>
                    ) : null}
                </div>

                {/* Sets grid */}
                {filteredSets.length === 0 ? (
                    <GlassCard className="p-10 text-center">
                        <Layers className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                        <p className="text-gray-600 dark:text-neutral-300">
                            {t('flashcards.noSets')}
                        </p>
                    </GlassCard>
                ) : (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredSets.map((set) => (
                            <GlassCard
                                key={set.id}
                                as="link"
                                href={`/flashcards/${set.slug}/`}
                                className="group overflow-hidden"
                            >
                                <div className="relative flex aspect-[16/7] items-center justify-center bg-theme-primary/10">
                                    <GraduationCap className="h-12 w-12 text-theme-primary-text opacity-70 transition-transform duration-300 group-hover:scale-110" />
                                    <span className="absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r from-theme-primary to-theme-secondary" />
                                </div>
                                <div className="space-y-2 p-4">
                                    <div className="flex items-center gap-2">
                                        {set.level ? (
                                            <span className="rounded bg-theme-primary px-2 py-0.5 text-xs font-bold text-theme-on-primary">
                                                {set.level}
                                            </span>
                                        ) : null}
                                        <span className="text-xs text-gray-500 dark:text-neutral-400">
                                            {t('flashcards.cards', { count: set.card_count })}
                                        </span>
                                    </div>
                                    <h3 className="line-clamp-2 font-semibold text-gray-800 dark:text-white">
                                        {set.title}
                                    </h3>
                                    {set.theme ? (
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            {set.theme}
                                        </p>
                                    ) : null}
                                </div>
                            </GlassCard>
                        ))}
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const Index: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <IndexContent />
        </ThemeWrapper>
    );
};

export default Index;
